import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { CSSProperties } from 'styled-components';
import styled, { css } from 'styled-components';

import { Link } from '@sorare/routing';

import { Rarity, Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { ANY_SPORT_MY_CLUB_SLUG_COLLECTIONS_COLLECTION } from '@sorare/core/src/constants/__generated__/routes';
import { useEventsContext } from '@sorare/core/src/contexts/events';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { getRarityRGBColor } from '@sorare/core/src/lib/colors';

import { ProgressBar } from 'components/collections/CollectionEvent/ProgressBar';
import { TeamLogoWithSeasonOverlay } from 'components/collections/CollectionEvent/TeamLogoWithSeasonOverlay';

import type { CollectionEventProgress_cardCollection } from './__generated__/index.graphql';

export const ALL_STAR_THRESHOLDS = {
  [Rarity.limited]: 150,
  [Rarity.rare]: 150,
  [Rarity.super_rare]: 75,
  [Rarity.unique]: 75,
} as Record<
  | typeof Rarity.limited
  | typeof Rarity.rare
  | typeof Rarity.super_rare
  | typeof Rarity.unique,
  number
>;

export const POSTSEASON_THRESHOLDS = {
  [Rarity.limited]: 180,
  [Rarity.rare]: 180,
  [Rarity.super_rare]: 100,
  [Rarity.unique]: 75,
} as Record<
  | typeof Rarity.limited
  | typeof Rarity.rare
  | typeof Rarity.super_rare
  | typeof Rarity.unique,
  number
>;

const TeamProgressBar = styled.div<{ pct: number }>`
  flex: 1;
  align-self: flex-start;

  &.flat {
    --active-bg-color: var(--c-rarity);
    --animated-border-bottom: 2px solid var(--c-active-tab-border);
    --button-border-radius: 24px 24px 0 0;
  }

  ${props =>
    props.pct >= 1
      ? css`
          --progress-color: rgba(var(--c-rarity), 1);
        `
      : css`
          --progress-color: rgba(var(--c-rarity), ${0.6 * props.pct});
          .isCollectionLink:hover & {
            --progress-color: rgba(var(--c-rgb-brand), 1);
          }
        `}
`;

type Props = {
  userSlug: string;
  collection: CollectionEventProgress_cardCollection;
  thresholds: typeof ALL_STAR_THRESHOLDS | typeof POSTSEASON_THRESHOLDS;
};

export const CollectionEventProgress = ({
  userSlug,
  collection,
  thresholds,
}: Props) => {
  const { generateSportContextPath } = useSportContext();
  const { track } = useEventsContext();

  const collectionRarity = collection.rarity! as
    | 'limited'
    | 'rare'
    | 'super_rare'
    | 'unique';
  if (!collection.userCardCollection || !thresholds[collectionRarity]) {
    return null;
  }
  const pct =
    Math.round(
      (collection.userCardCollection.score / thresholds[collectionRarity]) * 100
    ) / 100;
  const boost = Math.pow(1 - pct, 2);
  const pctBoosted = pct * (1 + boost);
  const valueBoost = Math.round(thresholds[collectionRarity] * pctBoosted);

  return (
    <Horizontal
      as={Link}
      to={generateSportContextPath(
        ANY_SPORT_MY_CLUB_SLUG_COLLECTIONS_COLLECTION,
        {
          params: {
            slug: userSlug,
            collection: collection.slug,
          },
        }
      )}
      onClick={() =>
        track('Clicking Collection Event CollectionItem', {
          sport: Sport.BASEBALL,
          filledSlots: collection.userCardCollection?.fulfilledSlotsCount ?? 0,
          rarity: collectionRarity,
          score: collection.userCardCollection?.score ?? 0,
          passThreshold: pct >= 1,
          season: collection.season!.startYear,
        })
      }
      className="isCollectionLink"
    >
      <TeamLogoWithSeasonOverlay
        team={collection.team!}
        season={collection.season!.startYear}
      />

      <TeamProgressBar
        pct={pctBoosted}
        style={
          {
            '--c-rarity': getRarityRGBColor(collectionRarity),
          } as CSSProperties
        }
      >
        <ProgressBar
          value={valueBoost}
          displayedValue={collection.userCardCollection.score}
          min={0}
          max={thresholds[collectionRarity]}
        />
      </TeamProgressBar>
    </Horizontal>
  );
};

CollectionEventProgress.fragments = {
  cardCollection: gql`
    fragment CollectionEventProgress_cardCollection on CardCollection {
      slug
      userCardCollection(forUserSlug: $userSlug) {
        id
        score
        fulfilledSlotsCount
      }
      season {
        id
        name
        startYear
      }
      rarity
      team: anyTeam {
        slug
        ...TeamLogoWithSeasonOverlay_team
      }
    }
    ${TeamLogoWithSeasonOverlay.fragments.team}
  ` as TypedDocumentNode<CollectionEventProgress_cardCollection>,
};
